import { styled } from '@mui/material'
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded'
import CheckRounded from '@mui/icons-material/CheckRounded'
import { intl } from '../../../../../../../../../i18n'
import { colorPalette } from '../../../../../../../../style/colorPalette'
import { FontSize } from '../../../../../../../../../styles/commonStyles'
import { Button } from '../../../../../../../../components/buttons'
import { EllipsisText } from '../../../../components/EllipsisText'
import { PAGE_TITLE_INPUT_MARGIN_TOP } from '../styles'

type PageTitleSuggestionButtonProps = {
  suggestedTitle: string | undefined
  isApplied: boolean
  onApply: () => void
}

/**
 * 集計範囲から組み立てたページタイトルの候補。
 *
 * 枠線に「候補」を食い込ませて囲むのは、入力済みの値と見分けるため。
 * 枠が無いと、黒字のタイトルが「もう入っている値」に見えてしまう。
 *
 * 枠と切り欠きの作り方は MUI の outlined な入力欄（NotchedOutline）に揃えてある。
 * 隣のページタイトル入力欄と高さを揃える必要があり、fieldset を素のまま並べると
 * legend が流れの中で1行ぶんの場所を取って枠線の位置が下にずれるため。
 * 詳細は Outline のコメントを参照。
 *
 * 候補なし・候補あり・反映済みで枠の位置が動かないようにしてある。
 * 位置が動くと、工程やチケット管理表を選び直すたびに下の項目がずれる。
 */
export const PageTitleSuggestionButton = ({
  suggestedTitle,
  isApplied,
  onApply,
}: PageTitleSuggestionButtonProps) => {
  if (!suggestedTitle) {
    return (
      <>
        <ApplyArrow />
        <Frame className="quiet">
          <NotchedOutline />
          <Row>
            <EmptyText>
              {intl.formatMessage({
                id: 'versionReport.pageConfig.pageTitle.suggestion.empty',
              })}
            </EmptyText>
          </Row>
        </Frame>
      </>
    )
  }
  if (isApplied) {
    return (
      <>
        <ApplyArrow />
        <Frame className="quiet">
          <NotchedOutline />
          <Row>
            <SuggestedTitle className="applied">
              {suggestedTitle}
            </SuggestedTitle>
            <AppliedMark>
              <CheckRounded sx={{ fontSize: '16px' }} />
              {intl.formatMessage({
                id: 'versionReport.pageConfig.pageTitle.suggestion.applied',
              })}
            </AppliedMark>
          </Row>
        </Frame>
      </>
    )
  }
  return (
    <>
      <ApplyArrow />
      <Frame>
        <NotchedOutline />
        <Row>
          {/* 反映される中身なので、枠の色に引きずられず本文として読める黒字にする */}
          <SuggestedTitle>{suggestedTitle}</SuggestedTitle>
          <Button colorPattern="skyBlue" onClick={onApply}>
            {intl.formatMessage({
              id: 'versionReport.pageConfig.pageTitle.suggestion.apply',
            })}
          </Button>
        </Row>
      </Frame>
    </>
  )
}

/**
 * 「候補」のラベルを食い込ませた枠線。
 *
 * MUI の outlined な入力欄と同じ作りにしてある。fieldset を absolute で枠全体に
 * かぶせ、legend は文字を透明にして切り欠きの幅だけを作らせる。読ませる文字は
 * 別の要素として切り欠きの上に重ねる。
 *
 * こうするのは、fieldset を素のまま置くと legend が流れの中で1行ぶんの高さを取り、
 * 要素の上端と枠線の上端がその高さぶんずれるため。absolute にすれば fieldset は
 * 外側のレイアウトに一切関与しないので、枠の位置と高さは Frame の指定だけで決まる。
 */
const NotchedOutline = () => (
  <>
    <Outline aria-hidden={true}>
      <OutlineLegend>
        <span>{suggestionLabel()}</span>
      </OutlineLegend>
    </Outline>
    <OutlineLabel>{suggestionLabel()}</OutlineLabel>
  </>
)

const suggestionLabel = () =>
  intl.formatMessage({
    id: 'versionReport.pageConfig.pageTitle.suggestion.label',
  })

/**
 * ラベルの縮小率。
 *
 * MUI の InputLabel は `font-size` を下げず、テーマの 12px のまま `scale(0.75)` で
 * 縮めて 9px に見せている。はじめから 9px で描くと、12px を縮めた字よりも細く潰れて
 * 読めなくなるので、同じやり方にそろえる
 */
const LABEL_SCALE = 0.75
/** 切り欠きに入る文字の大きさ。縮小後は入力欄のラベルと同じ 9px になる */
const LABEL_FONT_SIZE = FontSize.MEDIUM * LABEL_SCALE
/** 枠線に開ける切り欠き（legend）の高さ */
const LABEL_HEIGHT = 11
/**
 * ラベルを枠線より上に出す量。
 * 入力欄のラベル（MUI の InputLabel は `translate(14px, -9px)`）と同じ高さに置く
 */
const LABEL_OFFSET_TOP = 9
/** Outline の左右の padding。切り欠きはここから始まる */
const OUTLINE_PADDING_X = 8
/** legend 内の span の左右の padding。切り欠きを文字より広く取るぶん */
const LEGEND_SPAN_PADDING_X = 5
/**
 * 枠線を描く fieldset を、要素の上端より上にずらす量。
 *
 * fieldset の枠線は legend の中央に引かれるため、箱の上端より legend の高さの
 * 半分（約 5px）だけ下に描かれる。同じだけ上にずらして打ち消すと、描かれる枠線が
 * 箱の上端ちょうどに来る。MUI の outlined な入力欄（NotchedOutline）も
 * 同じ理由で `top: -5` を指定している
 */
const OUTLINE_OVERHANG = 5

const Frame = styled('div')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  boxSizing: 'border-box',
  // 隣の入力欄（PageTitleInput）と枠の位置・高さを揃える。
  // marginTop は入力欄と同じ定数を使う。高さは数値で持たず、同じ行の入力欄に合わせて伸ばす。
  // 入力欄の高さは MUI の padding と文字サイズから決まるので、こちらで数えると
  // テーマが変わったときに合わなくなる
  marginTop: `${PAGE_TITLE_INPUT_MARGIN_TOP}px`,
  alignSelf: 'stretch',
  padding: '0 10px',
})

const Outline = styled('fieldset')({
  position: 'absolute',
  // 下・左・右は Frame ぴったり。上だけ入力欄と同じだけはみ出させる
  inset: `${-OUTLINE_OVERHANG}px 0 0 0`,
  margin: 0,
  padding: `0 ${OUTLINE_PADDING_X}px`,
  borderRadius: '4px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: colorPalette.skyBlue[2],
  minWidth: '0%',
  textAlign: 'left',
  // 枠線は飾りなので、中身のクリックを邪魔しない
  pointerEvents: 'none',
  // 反映済みと候補なしは、操作するものではないので主張を弱める。
  // 塗りは Frame ではなくこちらに持たせる。Frame に持たせると、はみ出させた
  // 上の 5px だけ塗られずに白い帯が残る
  '.quiet > &': {
    borderColor: colorPalette.monotone[2],
    background: colorPalette.monotone[1],
  },
})

const OutlineLegend = styled('legend')({
  display: 'block',
  float: 'unset',
  width: 'auto',
  maxWidth: '100%',
  padding: 0,
  height: `${LABEL_HEIGHT}px`,
  fontSize: `${LABEL_FONT_SIZE}px`,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  // 文字は OutlineLabel 側で見せる。ここは切り欠きの幅を作るためだけに置いている
  visibility: 'hidden',
  '& > span': {
    display: 'inline-block',
    // 切り欠きを文字より少し広く取って、枠線と文字をくっつけない
    padding: `0 ${LEGEND_SPAN_PADDING_X}px`,
  },
})

/**
 * 切り欠きに重ねる「候補」の文字。
 *
 * 基準は Outline ではなく Frame。fieldset の中に絶対配置すると、ブラウザが
 * legend のために作る内部の箱が基準になってしまい、文字が枠の外に飛ぶ。
 */
const OutlineLabel = styled('span')({
  position: 'absolute',
  // 枠線は OUTLINE_OVERHANG のおかげで Frame の上端ちょうどに描かれる。その上に乗せる
  top: `${-LABEL_OFFSET_TOP}px`,
  // OutlineLegend が空ける切り欠きの、文字が始まる位置に合わせる
  left: `${OUTLINE_PADDING_X + LEGEND_SPAN_PADDING_X}px`,
  // 入力欄のラベルと同じ見え方にするため、12px を縮めて 9px にする
  fontSize: `${FontSize.MEDIUM}px`,
  lineHeight: 1.4375,
  transform: `scale(${LABEL_SCALE})`,
  transformOrigin: 'top left',
  whiteSpace: 'nowrap',
  color: colorPalette.monotone[5],
})

const Row = styled('div')({
  // Outline は絶対配置なので、何もしないと中身より手前に描かれる
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  minWidth: 0,
  // 反映ボタンが入っても枠の高さを超えないようにする
  '& button': {
    height: '28px',
  },
})

/**
 * 候補からページタイトルへ向かう矢印。
 *
 * 枠の外に置いて、左の入力欄と候補の関係だけを示す。候補の有無で出し入れすると
 * 枠の位置が横に動くので、常に出す。
 */
const ApplyArrow = styled(ArrowBackRounded)({
  fontSize: '18px',
  flexShrink: 0,
  color: colorPalette.monotone[4],
})

const SuggestedTitle = styled(EllipsisText)({
  flex: 1,
  minWidth: 0,
  // 隣の入力欄の文字に合わせる（MuiInputBase がテーマの 12px を指定している）
  fontSize: `${FontSize.MEDIUM}px`,
  color: colorPalette.monotone[10],
  '&.applied': {
    color: colorPalette.monotone[4],
  },
})

const EmptyText = styled('span')({
  flex: 1,
  minWidth: 0,
  fontSize: `${FontSize.MEDIUM}px`,
  color: colorPalette.monotone[4],
})

const AppliedMark = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
  fontSize: `${FontSize.MEDIUM}px`,
  color: colorPalette.monotone[4],
})
